import type { CanonicalEntity } from '@/shared/api/types';

/**
 * Pure (no three.js) reconcile primitives for the OPT-IN partial scene
 * re-build fast path.  Everything here operates on canonical entities and
 * their render hashes — never on Object3D — so it is trivially unit-testable
 * and can never accidentally corrupt the live render graph.
 *
 * SAFETY POSTURE: this module only ever *classifies* entities.  The caller
 * (useGltfLoader) decides whether to act on the classification and always
 * falls back to a full rebuild on any uncertainty.  The hash is deliberately
 * over-inclusive: including too much only causes an unchanged entity to be
 * re-built (correct, just slower) — it can never make a changed entity look
 * reusable (which would show stale geometry).
 */

/**
 * Deterministic JSON serialisation with recursively sorted object keys.
 * Sorting keys makes the hash stable across bundle serialisations that differ
 * only in key order (avoids spurious re-builds) without ever hiding a real
 * value change (which still alters the string).
 */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableStringify(v)).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  const parts = keys.map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`);
  return `{${parts.join(',')}}`;
}

/**
 * Stable hash of everything about an entity that can influence its rendered
 * Object3D.  Intentionally over-inclusive: the whole `render` block (center /
 * size / quaternion / primitive / color / opacity / material / visibleDefault
 * / imageUrl / aabb / sizeSource / …), the whole `domain` block (material
 * passport, visualProfile, facadeProfile, edges, emissive, …), plus kind /
 * name / parentId / moduleId, all of which are read while building the mesh
 * or are stamped into `userData`.
 *
 * NOTE: this hash is per-entity only.  Cross-entity build inputs (heavy-scene
 * threshold, module-envelope suppression) are handled by the caller, not
 * here — see useGltfLoader's incremental guard.
 */
export function entityRenderHash(entity: CanonicalEntity): string {
  const relevant = {
    kind: entity.kind ?? null,
    name: entity.name ?? null,
    parentId: entity.parentId ?? null,
    moduleId: entity.moduleId ?? null,
    render: entity.render ?? null,
    domain: entity.domain ?? null,
  };
  return stableStringify(relevant);
}

/** Build a fresh `id → renderHash` map for a set of entities. */
export function buildHashMap(entities: readonly CanonicalEntity[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const e of entities) map.set(e.id, entityRenderHash(e));
  return map;
}

export type EntitySetDiff = {
  /** Ids whose render hash is unchanged — their Object3D can be reused as-is. */
  reusableIds: string[];
  /** Ids present in both sets but whose render hash changed — rebuild only these. */
  rebuildIds: string[];
  /** Ids present before but gone now. */
  removedIds: string[];
  /** Ids present now but not before. */
  addedIds: string[];
  /** True when no ids were added or removed (only in-place changes, if any). */
  sameIdSet: boolean;
};

/**
 * Classify next entities against the previous build's hash map.
 *
 * - unchanged hash → `reusableIds`
 * - changed hash (same id) → `rebuildIds`
 * - id not seen before → `addedIds`
 * - id seen before but absent now → `removedIds`
 *
 * `sameIdSet` is true iff `addedIds` and `removedIds` are both empty, i.e. the
 * *membership* of the scene is identical and only per-entity edits (if any)
 * occurred.  The caller only attempts an incremental rebuild when this holds.
 */
export function diffEntitySets(
  prevHashById: ReadonlyMap<string, string>,
  nextEntities: readonly CanonicalEntity[],
): EntitySetDiff {
  const reusableIds: string[] = [];
  const rebuildIds: string[] = [];
  const addedIds: string[] = [];
  const nextIds = new Set<string>();

  for (const e of nextEntities) {
    nextIds.add(e.id);
    const nextHash = entityRenderHash(e);
    const prevHash = prevHashById.get(e.id);
    if (prevHash === undefined) {
      addedIds.push(e.id);
    } else if (prevHash === nextHash) {
      reusableIds.push(e.id);
    } else {
      rebuildIds.push(e.id);
    }
  }

  const removedIds: string[] = [];
  for (const id of prevHashById.keys()) {
    if (!nextIds.has(id)) removedIds.push(id);
  }

  const sameIdSet = addedIds.length === 0 && removedIds.length === 0;
  return { reusableIds, rebuildIds, removedIds, addedIds, sameIdSet };
}

/**
 * Identity-keyed reference counter for shared GPU resources (geometries,
 * textures, materials) so the incremental fast path can free a resource the
 * moment — and ONLY when — its last live referencing mesh is disposed.
 *
 * Deliberately three.js-agnostic: keys are opaque objects and disposal is the
 * caller's job.  The counter's only contract is the safety-critical one:
 *
 *   - `retain` is called once per mesh-reference to a resource at build time.
 *   - `release` returns `true` ONLY when the resource is tracked AND its count
 *     provably reaches 0.  An UNTRACKED resource (never retained, or already
 *     evicted) returns `false` → the caller must NOT dispose it.
 *
 * Consequences of this contract:
 *   - Over-counting (a stray extra `retain`) only leaks — never a premature
 *     dispose.  So when in doubt, the caller retains.
 *   - `release` on something unknown is a no-op-that-says-"don't dispose",
 *     which is exactly the conservative behaviour we want (worst case = the
 *     pre-existing bounded leak, never a use-after-dispose).
 *   - `clear` wipes all counts and is called on every FULL teardown, where the
 *     whole tree is disposed wholesale by a different (dispose-happy) path — so
 *     the counter must forget everything to avoid stale counts leaking into the
 *     next model's build.
 */
export class ResourceRefCounter {
  private readonly counts = new Map<object, number>();

  /** Register one live mesh-reference to `resource`. */
  retain(resource: object): void {
    this.counts.set(resource, (this.counts.get(resource) ?? 0) + 1);
  }

  /**
   * Drop one live mesh-reference to `resource`.
   * @returns `true` iff the resource was tracked and its count reached 0 (the
   *   caller now owns disposing + has had it evicted from the counter).  Returns
   *   `false` for untracked resources OR when live references remain — in both
   *   cases the caller MUST NOT dispose.
   */
  release(resource: object): boolean {
    const current = this.counts.get(resource);
    if (current === undefined) return false; // untracked → conservative: keep it
    if (current <= 1) {
      this.counts.delete(resource); // evict at zero
      return true;
    }
    this.counts.set(resource, current - 1);
    return false;
  }

  /** True if the resource currently has a tracked (non-zero) count. */
  has(resource: object): boolean {
    return this.counts.has(resource);
  }

  /** Current live-reference count for the resource (0 if untracked). */
  count(resource: object): number {
    return this.counts.get(resource) ?? 0;
  }

  /** Number of distinct tracked resources. */
  get size(): number {
    return this.counts.size;
  }

  /** Forget every count.  Call on full teardown / model change. */
  clear(): void {
    this.counts.clear();
  }
}
